import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  CompanyShippingAddress,
  CreateCompanyShippingAddressParams,
  UpdateCompanyShippingAddressParams,
  ListCompanyShippingAddressesResponseSchema,
  CreateCompanyShippingAddressResponseSchema,
  UpdateCompanyShippingAddressResponseSchema,
} from "@forgepoint/shared";

interface ShippingAddressLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
  getError: string | null;
  saveError: string | null;
  deleteError: string | null;
}

interface CompanyShippingAddressesStore {
  shippingAddresses: Map<string, CompanyShippingAddress>;
  loadingStates: Map<string, ShippingAddressLoadingState>;
  listLoading: boolean;
  listError: string | null;

  fetchByCompany: (companyId: string) => Promise<string[]>;
  create: (
    companyId: string,
    data: CreateCompanyShippingAddressParams,
  ) => Promise<CompanyShippingAddress | null>;
  update: (
    companyId: string,
    id: string,
    data: UpdateCompanyShippingAddressParams,
  ) => Promise<CompanyShippingAddress | null>;
  remove: (companyId: string, id: string) => Promise<boolean>;
}

const getInitialLoadingState = (): ShippingAddressLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingDelete: false,
  getError: null,
  saveError: null,
  deleteError: null,
});

export const useCompanyShippingAddressesStore =
  create<CompanyShippingAddressesStore>((set) => ({
    shippingAddresses: new Map(),
    loadingStates: new Map(),
    listLoading: false,
    listError: null,

    fetchByCompany: async (companyId: string) => {
      set({ listLoading: true, listError: null });

      try {
        const response = await fetchApi(
          `companies/${companyId}/shipping-addresses`,
          "GET",
        );

        if (response.ok && response.data) {
          const data = ListCompanyShippingAddressesResponseSchema.parse(
            response.data,
          );
          set((state) => {
            const addressMap = new Map(state.shippingAddresses);
            data.shippingAddresses.forEach((addr) => {
              addressMap.set(addr.id, addr);
            });
            return { shippingAddresses: addressMap, listLoading: false };
          });
          return data.shippingAddresses.map((addr) => addr.id);
        }

        set({
          listError: "Failed to fetch shipping addresses",
          listLoading: false,
        });
        return [];
      } catch (error) {
        captureException(error as Error);
        set({
          listError: "Failed to fetch shipping addresses",
          listLoading: false,
        });
        return [];
      }
    },

    create: async (
      companyId: string,
      data: CreateCompanyShippingAddressParams,
    ) => {
      try {
        const response = await fetchApi(
          `companies/${companyId}/shipping-addresses`,
          "POST",
          { body: data },
        );

        if (response.ok && response.data) {
          const result = CreateCompanyShippingAddressResponseSchema.parse(
            response.data,
          );
          set((state) => {
            const addressMap = new Map(state.shippingAddresses);
            addressMap.set(result.shippingAddress.id, result.shippingAddress);
            return { shippingAddresses: addressMap };
          });
          return result.shippingAddress;
        }

        return null;
      } catch (error) {
        captureException(error as Error);
        return null;
      }
    },

    update: async (
      companyId: string,
      id: string,
      data: UpdateCompanyShippingAddressParams,
    ) => {
      set((state) => {
        const loadingStates = new Map(state.loadingStates);
        const current = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, { ...current, isLoadingSave: true });
        return { loadingStates };
      });

      try {
        const response = await fetchApi(
          `companies/${companyId}/shipping-addresses/${id}`,
          "PUT",
          { body: data },
        );

        if (response.ok && response.data) {
          const result = UpdateCompanyShippingAddressResponseSchema.parse(
            response.data,
          );
          set((state) => {
            const addressMap = new Map(state.shippingAddresses);
            const loadingStates = new Map(state.loadingStates);
            addressMap.set(id, result.shippingAddress);
            loadingStates.set(id, {
              ...(loadingStates.get(id) || getInitialLoadingState()),
              isLoadingSave: false,
            });
            return { shippingAddresses: addressMap, loadingStates };
          });
          return result.shippingAddress;
        }

        set((state) => {
          const loadingStates = new Map(state.loadingStates);
          loadingStates.set(id, {
            ...(loadingStates.get(id) || getInitialLoadingState()),
            isLoadingSave: false,
            saveError: "Failed to update shipping address",
          });
          return { loadingStates };
        });
        return null;
      } catch (error) {
        captureException(error as Error);
        set((state) => {
          const loadingStates = new Map(state.loadingStates);
          loadingStates.set(id, {
            ...(loadingStates.get(id) || getInitialLoadingState()),
            isLoadingSave: false,
            saveError: "Failed to update shipping address",
          });
          return { loadingStates };
        });
        return null;
      }
    },

    remove: async (companyId: string, id: string) => {
      set((state) => {
        const loadingStates = new Map(state.loadingStates);
        const current = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, { ...current, isLoadingDelete: true });
        return { loadingStates };
      });

      try {
        const response = await fetchApi(
          `companies/${companyId}/shipping-addresses/${id}`,
          "DELETE",
        );

        if (response.ok) {
          set((state) => {
            const addressMap = new Map(state.shippingAddresses);
            const loadingStates = new Map(state.loadingStates);
            addressMap.delete(id);
            loadingStates.delete(id);
            return { shippingAddresses: addressMap, loadingStates };
          });
          return true;
        }

        set((state) => {
          const loadingStates = new Map(state.loadingStates);
          loadingStates.set(id, {
            ...(loadingStates.get(id) || getInitialLoadingState()),
            isLoadingDelete: false,
            deleteError: "Failed to delete shipping address",
          });
          return { loadingStates };
        });
        return false;
      } catch (error) {
        captureException(error as Error);
        set((state) => {
          const loadingStates = new Map(state.loadingStates);
          loadingStates.set(id, {
            ...(loadingStates.get(id) || getInitialLoadingState()),
            isLoadingDelete: false,
            deleteError: "Failed to delete shipping address",
          });
          return { loadingStates };
        });
        return false;
      }
    },
  }));
